import { format } from 'date-fns';
import { m } from 'framer-motion';
import { Box, Button, Stack, Typography } from '@mui/material';
import {
  clampTickHeight,
  GRADE_LABEL_ANIMATION_DURATION,
  GRADE_MAPPINGS,
  SELECTED_COLOR,
  type BarSegment,
  type GradeTestData,
  type NewSelection,
  type SelectedItem,
} from './constants';

type GradeLabelsProps = {
  animationDelay: number;
  grades: GradeTestData[];
  gradeBarSegments: BarSegment[];
  selectedItem: SelectedItem;
  setSelectedButton: (button: HTMLButtonElement) => void;
  setSelectedItem: (newSelection: NewSelection) => void;
};

export const GradeLabels = ({
  animationDelay,
  gradeBarSegments,
  grades,
  selectedItem,
  setSelectedButton,
  setSelectedItem,
}: GradeLabelsProps) => {
  const variants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  } as const;
  return (
    <m.div
      animate="visible"
      initial="hidden"
      transition={{ duration: GRADE_LABEL_ANIMATION_DURATION, delay: animationDelay }}
      variants={variants}
    >
      {gradeBarSegments.map(({ barBottom, barTop, value }) => {
        const isSelected = selectedItem.type === 'grade' && selectedItem.data.value === value;
        const grade = grades.find((grade) => grade.name === value);
        const fullGradeName =
          GRADE_MAPPINGS[grade?.name as keyof typeof GRADE_MAPPINGS]?.shortName ?? grade?.name;
        const date = grade ? format(grade.highScoreDate, 'M/d/yy') : '--';
        const testScore = grade ? formatGrade(grade.highScore) : '--';
        return (
          <GradeLabel
            gradeName={fullGradeName}
            height={clampTickHeight(barTop)}
            isSelected={isSelected}
            key={value}
            onClick={() =>
              setSelectedItem({
                type: 'grade',
                data: {
                  date,
                  height: (barBottom + barTop) / 2,
                  label: fullGradeName,
                  testScore,
                  value,
                },
              })
            }
            setSelectedButton={setSelectedButton}
          />
        );
      })}
    </m.div>
  );
};

function formatGrade(score: number): string {
  return `${Math.round(score * 100)}%`;
}

type GradeLabelProps = {
  gradeName: string;
  height: number;
  isSelected: boolean;
  onClick: (grade: string) => void;
  setSelectedButton: (button: HTMLButtonElement) => void;
};

const GradeLabel = ({
  gradeName,
  height,
  isSelected,
  onClick,
  setSelectedButton,
}: GradeLabelProps) => (
  <Button
    onClick={(e) => {
      onClick(gradeName);
      setSelectedButton(e.currentTarget);
    }}
    sx={{
      borderRadius: '12px 0 0 12px',
      bottom: height,
      position: 'absolute',
      p: 0,
      right: '100%',
      transform: 'translateY(50%)',
      ':hover': {
        bgcolor: '#C1D2FC60',
      },
    }}
  >
    <Stack
      alignItems="center"
      bgcolor={isSelected ? '#FAEBF5' : undefined}
      borderRadius="12px 0 0 12px"
      direction="row"
      gap={0.5}
      p="8px 0 8px 16px"
    >
      <Typography
        color={isSelected ? 'text.primary' : '#6C7589'}
        fontWeight={isSelected ? 700 : 600}
        fontSize={16}
        whiteSpace="nowrap"
      >
        {gradeName}
      </Typography>
      <Box
        height={isSelected ? '2px' : '1px'}
        width="17px"
        bgcolor={isSelected ? SELECTED_COLOR : 'text.secondary'}
      />
    </Stack>
  </Button>
);
